import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGetBadgeSummaryQuery } from 'store/services/badge/badgeApi';
import BadgeIcon from 'components/BadgeIcon/BadgeIcon';
import { TIER_FROM_LEVEL } from 'types/interfaces/badge';
import './ActivityUserBadgeFlyout.css';

interface ActivityUserBadgeFlyoutProps {
  userId: number;
  username: string;
  position: { x: number; y: number };
  onClose: () => void;
}

const FLYOUT_WIDTH = 200;
const FLYOUT_DEBOUNCE_MS = 200;

const ActivityUserBadgeFlyout: React.FC<ActivityUserBadgeFlyoutProps> = ({
  userId,
  username,
  position,
  onClose,
}) => {
  const [visible, setVisible] = useState(false);
  const flyoutRef = useRef<HTMLDivElement>(null);

  // Debounce appearance
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), FLYOUT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, []);

  // Close on scroll
  useEffect(() => {
    const handleScroll = () => onClose();
    const scrollContainer = document.querySelector('.main-page-content');
    scrollContainer?.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer?.removeEventListener('scroll', handleScroll);
  }, [onClose]);

  // Lazy-fetch displayed badges
  const { data: summary } = useGetBadgeSummaryQuery(userId);
  const badges = summary?.displayedBadges ?? [];

  if (!visible) return null;

  // Edge detection
  let left = position.x;
  let top = position.y;

  if (typeof window !== 'undefined') {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left + FLYOUT_WIDTH > viewportWidth - 10) {
      left = position.x - FLYOUT_WIDTH - 10;
    }

    const estimatedHeight = badges.length > 0 ? 120 : 60;
    if (top + estimatedHeight > viewportHeight - 10) {
      top = viewportHeight - estimatedHeight - 10;
    }
    if (top < 10) top = 10;
  }

  return createPortal(
    <div
      ref={flyoutRef}
      className="activity-badge-flyout"
      style={{ left, top }}
      onMouseEnter={(e) => e.stopPropagation()}
      onMouseLeave={onClose}
    >
      <div className="activity-badge-flyout__header">
        {username}&apos;s Badges
      </div>
      {badges.length === 0 ? (
        <div className="activity-badge-flyout__empty">No badges displayed</div>
      ) : (
        <div className="activity-badge-flyout__grid">
          {badges.map((ub) => (
            <div key={ub.id} className="activity-badge-flyout__badge" title={ub.badge.title}>
              <BadgeIcon
                icon={ub.badge.icon}
                shape={ub.badge.shape}
                tier={TIER_FROM_LEVEL[ub.badge.level] ?? 'bronze'}
                size={32}
                earned={true}
              />
            </div>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};

export default ActivityUserBadgeFlyout;
